import {FC} from 'react'
import {Content} from '@prismicio/client'
import {SliceComponentProps} from '@prismicio/react'
import {RatingsSection} from 'slices/HeroV2/RatingsSection'
import {ButtonV2} from 'src/common/ButtonV2'
import {getButtonsData} from 'src/common/helpers'
import {Context} from 'src/common/types'
import {useLinkResolver} from 'prismicio'
import {AssetsWrapper} from './AssetsWrapper'
import {LogoBarV2} from 'slices/LogoBar/v2/LogoBarV2'
import {IconTag} from 'src/common/IconTag'

/**
 * Props for `HeroV3`.
 */
export type HeroV3Props = SliceComponentProps<Content.HeroV3Slice> & {context: Context}

/**
 * Component for "HeroV3" Slices.
 */
const HeroV3: FC<HeroV3Props> = ({slice, context, index: componentIndex}) => {
  const {ratings} = slice.primary
  const {linkResolver} = useLinkResolver()
  const logos = slice.primary.logos.map((logo) => logo.logo)

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    primaryButton: slice.primary.primary_button,
    buttonsData: context.buttonsData,
    secondaryButton: slice.primary.secondary_button,
  })

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-green-dark"
      data-nav-dark="true"
    >
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-5 pb-9 pt-[137px] md:px-10 lg:px-5 xl:px-0">
        {/* Content */}
        <div className="flex w-full flex-col items-center gap-6">
          {ratings?.length > 1 ? (
            <RatingsSection ratings={ratings} type="Animated" centered version="v2" />
          ) : (
            <IconTag iconName={ratings[0]?.review_platform} label={ratings[0]?.label!} version="v2" />
          )}
          <h1 className="text-center text-[72px] font-normal leading-[74px] tracking-[-.72px] text-moss-white">
            {slice.primary.heading}
          </h1>
          <p className="text-tg-body-l max-w-[432px] text-center text-moss-white">{slice.primary.description}</p>
          <div className="flex flex-col gap-3 md:flex-row md:gap-4">
            <ButtonV2
              className="uppercase"
              variant="primary"
              size="medium"
              trackingData={{
                location: `component-${componentIndex}-hero-v3`,
                dataType: 'primary-button',
                pageName: context.pageUid,
                mktgId: primaryButtonData?.marketing_id || '',
              }}
              href={linkResolver(primaryButtonData?.link)}
            >
              {primaryButtonData?.label}
            </ButtonV2>
            {secondaryButtonData && (
              <ButtonV2
                className="uppercase"
                variant="secondary"
                size="medium"
                trackingData={{
                  location: `component-${componentIndex}-hero-v3`,
                  dataType: 'secondary-button',
                  pageName: context.pageUid,
                  mktgId: secondaryButtonData?.marketing_id || '',
                }}
                href={linkResolver(secondaryButtonData?.link)}
              >
                {secondaryButtonData?.label}
              </ButtonV2>
            )}
          </div>
          <AssetsWrapper assets={slice.primary.assets} />
          <LogoBarV2 title={slice.primary.logobar_text} logos={logos} />
        </div>
      </div>
    </section>
  )
}

export default HeroV3
